/**
 * Marketing-site URL configuration.
 *
 * The auth pages need a fallback target for the "Back" button when the
 * user arrived via a bookmark, a new tab, or any other entry that
 * leaves `window.history` too shallow to navigate back. The button
 * jumps to the marketing homepage instead of dropping the user on a
 * blank tab or the current auth screen.
 *
 * Env-var contract mirrors the sibling repos:
 *   - Read `VITE_GREENCHARGE_MAIN_URL` at build time.
 *   - Fall back to a dev-friendly default (localhost:5173, the vite
 *     default port for greencharge-main in dev) so a developer running
 *     both apps side-by-side gets the correct behaviour without
 *     touching .env files.
 *   - Warn (error in production) when the var is unset so a mis-
 *     configured deploy is visible in the console.
 */

const DEV_FALLBACK_MARKETING_URL = 'http://localhost:5173';

const RAW_MARKETING_URL =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GREENCHARGE_MAIN_URL) || null;

// Strip any trailing slashes so downstream URL construction stays
// consistent (`${MARKETING_URL}/some/path` never yields `//`).
export const MARKETING_URL = (RAW_MARKETING_URL || DEV_FALLBACK_MARKETING_URL).replace(/\/+$/, '');

if (!RAW_MARKETING_URL && typeof console !== 'undefined') {
    const msg =
        '[sw-ev-user] VITE_GREENCHARGE_MAIN_URL is not set. Auth Back button will fall back to ' +
        DEV_FALLBACK_MARKETING_URL +
        ' — set VITE_GREENCHARGE_MAIN_URL in .env before building for production.';
    if (typeof import.meta !== 'undefined' && import.meta.env?.PROD) {
        console.error(msg);
    } else {
        console.warn(msg);
    }
}

/**
 * Safely join the marketing base URL with a route path.
 *
 * MARKETING_URL is already normalised without trailing slashes (see the
 * `.replace(/\/+$/, '')` above) — this helper additionally strips any
 * leading slash on `path` so callers can pass either `'/terms'` or
 * `'terms'` without producing `//` in the final URL.
 *
 * Kept as a pure function so tests can call it directly with a
 * synthetic base without spinning up the module-level MARKETING_URL.
 *
 * @param {string} base - Marketing site base URL (no trailing slash)
 * @param {string} path - Route path, with or without a leading slash
 * @returns {string}
 */
export function joinMarketingUrl(base, path) {
    const normalisedBase = String(base ?? '').replace(/\/+$/, '');
    const normalisedPath = String(path ?? '').replace(/^\/+/, '');
    return normalisedPath ? `${normalisedBase}/${normalisedPath}` : normalisedBase;
}

/**
 * Canonical marketing-site route paths. Kept as a single-source enum so
 * every auth surface links to the same URL and a future path rename
 * only touches this file. Values verified against
 * greencharge-main/src/App.jsx routes on 2026-07-18 (`/terms` and
 * `/privacy` are the real, mounted routes — not `/terms-of-service`
 * or `/privacy-policy`).
 */
export const MARKETING_ROUTES = Object.freeze({
    TERMS: '/terms',
    PRIVACY: '/privacy',
});

/** Fully-qualified URL for the marketing terms-of-service page. */
export const TERMS_URL = joinMarketingUrl(MARKETING_URL, MARKETING_ROUTES.TERMS);

/** Fully-qualified URL for the marketing privacy-policy page. */
export const PRIVACY_URL = joinMarketingUrl(MARKETING_URL, MARKETING_ROUTES.PRIVACY);
